import * as Model from "../../../model"
import * as LoanDomain from "../../../domain/sfp/loan-domain"
import ScenarioService from "../../../service/sfp/scenario-service"
import Operations from "../../scenarii/common/operations"
import * as Tabledefinition from "../../scenarii/detail/table-definition"

/**
 * Contexte de calcul du fonctionnement partagé par les sections du rapport de
 * gestion, pour reprendre exactement les chiffres de la Synthèse du scénarii
 * de fonctionnement (même scénario, même bascule rétrospectif / prospectif,
 * même moteur de calcul).
 */
export interface ScenarioContext {
	localAuthority: Model.LocalAuthority
	scenario: Model.Scenario
	previousScenario: Model.Scenario
	operations: Operations
	propsloan: any
}

/**
 * Le scénario retenu est le scénario validé de l'année de référence, à défaut
 * celui de l'année en cours. Pour un scénario de l'année suivante, l'année en
 * cours est reprise du scénario validé de l'année en cours, comme la Synthèse.
 */
export async function createScenarioContext(
	localAuthority: Model.LocalAuthority,
	ppi: Model.Ppi,
	referenceYear: number,
	propsloan: any
): Promise<ScenarioContext> {
	const currentYear = new Date().getFullYear()
	const scenarios: Model.Scenario[] = await ScenarioService.findScenariiByLAId(localAuthority.id)
	const scenario = scenarios.find(o => o.year == referenceYear && o.validated == true)
		|| scenarios.find(o => o.year == currentYear && o.validated == true)
		|| null
	const previousScenario = scenario && scenario.year == currentYear + 1
		? scenarios.find(o => o.year == scenario.year - 1 && o.validated == true) || null
		: null
	const operations = scenario ? new Operations(scenario, ppi, localAuthority) : null

	return { localAuthority, scenario, previousScenario, operations, propsloan }
}

/** Bascule rétrospectif / prospectif identique à la Synthèse. */
export function getOperationType(
	ctx: ScenarioContext,
	year: number
): Tabledefinition.OperationType {
	if (!ctx.scenario) return "retrospective"
	return year < ctx.scenario.year && (year !== ctx.scenario.year - 1 || !ctx.previousScenario)
		? "retrospective" : "prospective"
}

export function getScenarioYear(ctx: ScenarioContext, year: number): Model.ScenarioYear {
	return ctx.scenario ? (ctx.scenario.years || []).find(sy => sy.year === year) : undefined
}

export function getTotalInterest(ctx: ScenarioContext, year: number): number {
	return LoanDomain.getTotalInterestByYear(ctx.propsloan, year)
}

/** Montant d'un compte de fonctionnement, identique à la Synthèse. */
export function findAccountValue(
	ctx: ScenarioContext,
	laca: Model.LocalAuthorityChapterAccount,
	receipts: boolean,
	year: number
): number {
	return Tabledefinition._findOperationAccountValue(
		getOperationType(ctx, year),
		year,
		ctx.localAuthority,
		ctx.operations,
		laca,
		receipts,
		ctx.previousScenario,
		[],
		getTotalInterest(ctx, year),
		getScenarioYear(ctx, year),
		ctx.localAuthority.isDebtSimplified
	)
}

/** Comptes de fonctionnement retenus par la Synthèse pour un sens donné. */
export function getOperationAccounts(
	ctx: ScenarioContext,
	receipts: boolean
): Model.LocalAuthorityChapterAccount[] {
	return Tabledefinition._getFilteredOperationAccounts(ctx.localAuthority, receipts)
}

/** « Total des dépenses » / « Total des recettes » de la Synthèse. */
export function getOperationTotal(ctx: ScenarioContext, receipts: boolean, year: number): number {
	return getOperationAccounts(ctx, receipts)
		.reduce((total, laca) => total + findAccountValue(ctx, laca, receipts, year), 0)
}

/** « Dépenses réelles de fonctionnement » de la Synthèse. */
export function getRealExpense(ctx: ScenarioContext, year: number, loans: Model.PpiYearLoan[]): number {
	return Tabledefinition._aggregateRealOperations1(
		getOperationType(ctx, year),
		year,
		ctx.localAuthority,
		ctx.operations,
		false,
		ctx.previousScenario,
		[],
		loans,
		getTotalInterest(ctx, year),
		getScenarioYear(ctx, year),
		ctx.localAuthority.isDebtSimplified
	)
}

/** « Recettes réelles de fonctionnement » de la Synthèse. */
export function getRealReceipts(ctx: ScenarioContext, year: number, loans: Model.PpiYearLoan[]): number {
	return Tabledefinition._aggregateRealOperations(
		getOperationType(ctx, year),
		year,
		ctx.localAuthority,
		ctx.operations,
		true,
		ctx.previousScenario,
		[],
		loans
	)
}



// WEBPACK FOOTER //
// ./src/component/management-report/generator/scenario-context.ts